// src/Features/Projects/components/ProjectCreateForm/ProjectCreateForm.tsx
import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useNavigate } from "react-router-dom";
import {
  PlusIcon,
  SparklesIcon,
  DocumentTextIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { useAuth } from "@/contexts/AuthContext";
import { FormInput, FormTextarea, FormButton } from "@/components";
import {
  useProjectMutations,
  useGeminiMutations,
  projectDraftSchema,
  geminiService,
} from "@/features/Projects";
import type { ProjectDraftFormData } from "@/features/Projects";
import type { ProjectCreateFormProps } from "./projectCreateForm.types";
import { FORM_PLACEHOLDERS } from "./projectCreateForm.definitions";

export default function ProjectCreateForm({
  onSuccess,
  onCancel,
}: ProjectCreateFormProps) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { create, isCreating } = useProjectMutations();
  const { expandDescription, isExpandingDescription } = useGeminiMutations();

  // Estados para controlar o fluxo
  const [step, setStep] = useState<"draft" | "expanded">("draft");
  const [expandedDescription, setExpandedDescription] = useState("");
  const [finalName, setFinalName] = useState("");

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    watch,
    setValue,
    getValues,
  } = useForm<ProjectDraftFormData>({
    resolver: yupResolver(projectDraftSchema),
    mode: "onChange",
    defaultValues: {
      name: "",
      initialDescription: "",
      suggestedName: "",
    },
  });

  const watchedDescription = watch("initialDescription");
  const watchedName = watch("name");
  const canExpandDescription =
    watchedDescription.length >= 10 && geminiService.isConfigured();

  // Expandir descrição com Gemini
  const handleExpandDescription = async () => {
    const currentDescription = getValues("initialDescription");
    const currentName = getValues("name");

    if (!currentDescription) return;

    try {
      const response = await expandDescription({
        initialDescription: currentDescription,
      });

      // Se IA sugeriu um nome e o campo está vazio, usar sugerido
      let nameToUse = currentName;
      if (response.suggestedName && !currentName.trim()) {
        nameToUse = response.suggestedName;
        setValue("name", response.suggestedName);
        setValue("suggestedName", response.suggestedName);
      }

      setFinalName(nameToUse);
      setExpandedDescription(response.expandedDescription);
      setStep("expanded");
    } catch (error) {
      console.error("Erro ao expandir descrição:", error);
    }
  };

  // Criar projeto com descrição expandida
  const handleCreateProject = async () => {
    try {
      const projectData = {
        name: finalName || watchedName,
        description: expandedDescription,
      };

      const projectId = await create(projectData);
      onSuccess?.(projectId);
      navigate(`/projects/${projectId}`);
    } catch (error) {
      console.error("Erro ao criar projeto:", error);
    }
  };

  // Voltar para editar
  const handleBackToEdit = () => {
    setStep("draft");
  };

  // Cancelar operação
  const handleCancel = () => {
    const confirmed = window.confirm(
      "Deseja realmente cancelar? Todas as informações serão perdidas."
    );
    if (!confirmed) return;

    onCancel?.();
    navigate("/projects");
  };

  const isLoading = isCreating || isExpandingDescription;

  // Renderização condicional baseada no step
  if (step === "expanded") {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-2xl shadow-raro border border-primary-100">
          {/* Header */}
          <div className="px-8 py-6 border-b border-primary-100">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="flex items-center justify-center w-12 h-12 bg-primary-100 rounded-xl">
                  <SparklesIcon className="w-6 h-6 text-primary-600" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-raro-gray-900 font-montserrat">
                    Projeto Expandido pela IA
                  </h1>
                  <p className="text-sm text-raro-gray-600">
                    Revise e ajuste a descrição antes de criar
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Conteúdo */}
          <div className="p-8 space-y-6">
            {/* Nome do projeto */}
            <div>
              <label className="block text-sm font-medium text-raro-gray-700 mb-2">
                Nome do Projeto
              </label>
              <input
                type="text"
                value={finalName}
                onChange={(e) => setFinalName(e.target.value)}
                className="w-full px-4 py-3 border border-raro-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 font-montserrat"
                placeholder="Nome do projeto..."
              />
            </div>

            {/* Descrição expandida */}
            <div>
              <label className="block text-sm font-medium text-raro-gray-700 mb-2">
                Descrição Expandida pela IA
              </label>
              <textarea
                value={expandedDescription}
                onChange={(e) => setExpandedDescription(e.target.value)}
                rows={12}
                className="w-full px-4 py-3 border border-raro-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 font-montserrat resize-none"
                placeholder="Descrição expandida..."
              />
              <div className="mt-2 text-xs text-raro-gray-500">
                ✨ Você pode editar a descrição gerada pela IA antes de criar o projeto
              </div>
            </div>

            {/* Botões de ação */}
            <div className="flex space-x-4 pt-6 border-t border-primary-100">
              <FormButton
                type="button"
                variant="secondary"
                size="lg"
                onClick={handleBackToEdit}
                disabled={isLoading}
              >
                ← Voltar para Editar
              </FormButton>

              <FormButton
                type="button"
                variant="secondary"
                size="lg"
                onClick={handleCancel}
                disabled={isLoading}
              >
                Cancelar
              </FormButton>

              <FormButton
                type="button"
                variant="primary"
                size="lg"
                onClick={handleCreateProject}
                loading={isCreating}
                disabled={isLoading || !finalName.trim() || !expandedDescription.trim()}
                icon={<DocumentTextIcon className="w-5 h-5" />}
                iconPosition="left"
                fullWidth
              >
                Criar Projeto
              </FormButton>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Step inicial - formulário de rascunho
  return (
    <div className="max-w-4xl mx-auto">
      <div className="bg-white rounded-2xl shadow-raro border border-primary-100">
        {/* Header */}
        <div className="px-8 py-6 border-b border-primary-100">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="flex items-center justify-center w-12 h-12 bg-primary-100 rounded-xl">
                <PlusIcon className="w-6 h-6 text-primary-600" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-raro-gray-900 font-montserrat">
                  Novo Projeto
                </h1>
                <p className="text-sm text-raro-gray-600">
                  Descreva sua ideia e deixe a IA expandir os detalhes
                </p>
              </div>
            </div>

            {geminiService.isConfigured() && (
              <div className="flex items-center space-x-2 px-3 py-2 bg-primary-50 rounded-lg">
                <SparklesIcon className="w-4 h-4 text-primary-600" />
                <span className="text-xs font-medium text-primary-700">
                  IA Ativada
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Form */}
        <form
          onSubmit={handleSubmit(handleExpandDescription)}
          className="p-8 space-y-6"
        >
          {/* Nome do projeto */}
          <div>
            <FormInput
              label="Nome do Projeto"
              placeholder={FORM_PLACEHOLDERS.name}
              error={errors.name?.message}
              required
              disabled={isLoading}
              {...register("name")}
            />

            {/* Mostrar nome sugerido se houver */}
            {getValues("suggestedName") &&
              getValues("suggestedName") !== getValues("name") && (
                <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-xs text-blue-700">
                    💡 <strong>Sugestão da IA:</strong>{" "}
                    {getValues("suggestedName")}
                    <button
                      type="button"
                      onClick={() =>
                        setValue("name", getValues("suggestedName") || "", {
                          shouldValidate: true,
                        })
                      }
                      className="ml-2 text-blue-600 underline hover:text-blue-800"
                    >
                      Usar esta sugestão
                    </button>
                  </p>
                </div>
              )}
          </div>

          {/* Descrição inicial */}
          <div>
            <FormTextarea
              label="Descrição Inicial"
              placeholder={FORM_PLACEHOLDERS.initialDescription}
              error={errors.initialDescription?.message}
              required
              disabled={isLoading}
              rows={6}
              showCharCount
              maxLength={2000}
              {...register("initialDescription")}
            />

            <div className="mt-2 text-xs text-raro-gray-500">
              💡 Descreva sua ideia de forma clara. A IA irá expandir e detalhar
              sua proposta automaticamente.
            </div>
          </div>

          {/* Actions */}
          <div className="flex space-x-4 pt-6 border-t border-primary-100">
            <FormButton
              type="button"
              variant="secondary"
              size="lg"
              onClick={handleCancel}
              disabled={isLoading}
            >
              Cancelar
            </FormButton>

            <FormButton
              type="submit"
              variant="primary"
              size="lg"
              loading={isExpandingDescription}
              disabled={isLoading || !isValid || !canExpandDescription}
              icon={<SparklesIcon className="w-5 h-5" />}
              iconPosition="left"
              fullWidth
            >
              Expandir com IA
            </FormButton>
          </div>

          {/* Dica sobre IA */}
          {!geminiService.isConfigured() && (
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl">
              <div className="flex items-start space-x-3">
                <SparklesIcon className="w-5 h-5 text-amber-600 mt-0.5" />
                <div>
                  <h4 className="font-medium text-amber-800 font-montserrat">
                    IA não configurada
                  </h4>
                  <p className="text-sm text-amber-700 mt-1">
                    Configure a API do Gemini para usar expansão automática de
                    descrições.
                  </p>
                </div>
              </div>
            </div>
          )}
        </form>
      </div>
    </div>
  );
}